import { useEffect, useState, useContext, useRef } from 'react';

import { LazyQueryResult, OperationVariables, useQuery } from '@apollo/client';

import { AnalyticsContext } from 'lane-shared/contexts';
import jwtDecode from 'jwt-decode';

import { LaneType } from 'common-types';
import { pause } from 'async-timing';
import { getClient } from '../apollo';
import LaunchDarklyContext from '../contexts/LaunchDarklyContext';
import { RootLevelFlagsContext } from '../contexts/RootLevelFlagContext/RootLevelFlagsContextProvider';
import { initializeUser } from '../graphql/query';
import { updateUser } from '../graphql/user';
import { isNotLoggedInError } from '../helpers';
import Storage, { StorageKeys } from '../helpers/Storage';
import { createAndUploadMedia } from '../helpers/createAndUploadMedia';
import emitter, {
  EVENT_AUTH_TOKEN_INVALID,
  EVENT_LOGOUT,
  EVENT_AUTH_TOKEN,
  EVENT_AUTH_BEGIN_LOGOUT,
  EVENT_UPLOAD_PROFILE_PHOTO,
} from '../helpers/emitter';
import hasPermission from '../helpers/hasPermission';
import i18n from 'localization';
import { ImageType } from '../types/ImageType';
import { UserType } from '../types/User';
import { LibraryTypeEnum } from 'constants-content';
import { MediaImageContentTypeEnum, MediaTypeEnum } from 'constants-media';
import { PermissionKey } from 'constants-permissions';
import { GetAuthTokenResponse } from '../types/auth0/getAuthTokenResponseType';

type UseUserDataProps = {
  jti?: string;
  jwt?: string;
  blocks?: any[] | null;
  fetchBlocks?:
    | (() => Promise<LazyQueryResult<any, OperationVariables>>)
    | null;
  getAuthToken: GetAuthTokenResponse;
  /**
   * When true, storeToken will skip writing to App Storage (StorageKeys.AUTH_TOKEN).
   * Used when Auth0 is enabled and consolidate-auth-token-storage flag is ON,
   * since Auth0 manages the token directly.
   */
  skipStorageWrite?: boolean;
  /**
   * Opt-in for the mobile-only consolidate cold-start recovery (the
   * flag-hydration token re-fetch). Web must NOT enable it: web already
   * recovers a dead/rotated refresh token via its own TS-41719 mount-effect
   * (loginWithRedirect over the SSO session). Running the re-fetch on web fires
   * concurrent getAuthToken() retries that collide with that mount effect at
   * the auth0-spa-js token lock, so getAccessTokenSilently times out instead of
   * returning invalid_grant, recovery is never classified as unrenewable, and
   * the app hangs on a loader. Defaults false → web behaves exactly like main.
   */
  enableConsolidateColdStartRecovery?: boolean;
};

let instances = 0;

// How many times to retry the me query when the consolidate-flow
// getAuthToken probe comes back empty (SDK hydrating / refresh in flight)
// before treating the session as genuinely invalid. ~3s of grace.
const MAX_PROBE_RETRIES = 3;
const PROBE_RETRY_INTERVAL_MS = 1000;

// Cold-start token hydration grace. Under the consolidate flag the
// token is not persisted to Storage (Auth0 owns it), so checkStorage() must get
// it from getAuthToken(). On a relaunch where the access token expired, that
// call returns null while the Auth0 SDK hydrates / the refresh-token exchange is
// in flight. checkStorage runs once, so a transient null there would finalize
// authToken=null and the nav gate would route a still-valid session to Welcome
// (login page). Re-probe a few times so the SDK can settle first. ~5s of grace
// (refresh round-trip is well under this); bounded so a genuinely logged-out
// user still reaches Welcome promptly and never hangs on a loader.
const MAX_COLD_START_TOKEN_RETRIES = 5;
const COLD_START_TOKEN_RETRY_INTERVAL_MS = 1000;

/**
 * Helper hook to set the current user, this should only be used once per app
 * . i.e. at the top level of the mobile app, or the top level of the web app.
 *
 * If you are looking for the current logged in user, use the UserDataContext
 *
 * This needs to
 *  - check for an existing authToken
 *  - get the user data from the API using that authToken
 *  - poll for the user data periodically to keep it up to date.
 *  - shut off when a logout event occurs
 *  - turn on when a login event occurs.
 */
export default function useUserData({
  jwt,
  jti,
  blocks,
  fetchBlocks,
  getAuthToken,
  skipStorageWrite = false,
  enableConsolidateColdStartRecovery = false,
}: UseUserDataProps) {
  const [user, setUser] = useState<UserType | null>(null);
  const [sessionId, setSessionId] = useState<LaneType.UUID | null>(null);
  const [route, setRoute] = useState<string | null>(null);
  const [authWarnings, setAuthWarnings] = useState<string[] | null>(null);
  const [authToken, setAuthToken] = useState<{ jti: string } | string | null>(
    null
  );
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Counts retries of the consolidate-flow getAuthToken probe. On cold
  // start getAuthToken returns null while the Auth0 SDK is still hydrating
  // (isLoading) or a refresh is in flight — a transient state, NOT a dead
  // session. We retry the me query a few times before invalidating so a
  // transient null can't eject an otherwise-valid session (spurious logout /
  // navigation thrash → infinite spinner).
  const probeRetryRef = useRef(0);
  const skipStorageWriteRef = useRef(skipStorageWrite);
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
    };
  }, []);
  // has the app initialized, i.e. checked the stored for an existing auth token
  const [isInitialized, setIsInitialized] = useState(false);
  // have we attempted a login yet
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  // is there a current login attempt happening now
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  // is there a current login
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // true when we invalidated the token due to me query auth error (e.g. expired/blocked). Used to
  // distinguish expired session (clear Auth0) from new SSO signup (redirect to signup).
  const [sessionInvalidatedByMeError, setSessionInvalidatedByMeError] =
    useState(false);
  // TS-38584
  const [meQuerySucceeded, setMeQuerySucceeded] = useState(false);
  const { client: lDClient } = useContext(LaunchDarklyContext);
  const { flags: rootLevelFlags } = useContext(RootLevelFlagsContext);
  const analytics = useContext(AnalyticsContext);

  useEffect(() => {
    skipStorageWriteRef.current = skipStorageWrite;
  }, [skipStorageWrite]);

  const isConsolidateAuthTokenStorageOn =
    rootLevelFlags.useAuth0ConsolidateAuthTokenStorage;

  // getAuthToken is recreated every render and reads the LIVE
  // consolidate flag. The mount-time checkStorage closure captures a stale copy
  // (flag still false before LaunchDarkly hydrates), so we keep the latest one
  // in a ref for the flag-hydration recovery effect below.
  const getAuthTokenRef = useRef(getAuthToken);

  getAuthTokenRef.current = getAuthToken;
  // Guards the one-shot flag-hydration token re-fetch so it runs at most once.
  const coldStartTokenRefetchedRef = useRef(false);
  // True while an intentional logout is underway. The flag-hydration
  // re-fetch below would otherwise re-mint a token from surviving Auth0 creds
  // (logout clears app state/Storage/cookies but auth0Logout races) and log the
  // user right back in. Set on EVENT_AUTH_BEGIN_LOGOUT / EVENT_LOGOUT, cleared on
  // a genuine new login (EVENT_AUTH_TOKEN).
  const loggingOutRef = useRef(false);

  // Cold-start flag-hydration recovery. RootLevelFlags initialize to
  // all-false and populate asynchronously from LaunchDarkly. If they hydrate
  // AFTER the one-shot checkStorage() ran, checkStorage saw the consolidate flag
  // as false, took the legacy `Storage.getItem(AUTH_TOKEN)` path (empty under
  // consolidate), finalized authToken=null + isInitialized=true, and the nav
  // gate routed a valid Auth0 session to Welcome (login page) — the reported
  // "logout". Nothing repopulates authToken afterward (me is skip:!authToken),
  // so the user is stuck on login. When the flag finally flips true with no
  // token yet and the user not logged in, re-fetch the token via the LIVE
  // getAuthToken (ref — the mount closure captured the stale flag-false copy),
  // which now takes the Auth0 path, and set authToken so me re-runs → home.
  // One-shot so a genuine mid-session logout is never resurrected.
  //
  // This path REPLACES checkStorage's cold-start probe on late hydration: that
  // probe (and its 5×1s retries to let the Auth0 SDK settle) is gated on the
  // mount-captured flag, so it is skipped whenever flags arrive after mount.
  // Mirror its retry loop here — the Auth0 token exchange may still be in flight
  // when the flag flips, so a single getAuthToken() can transiently return null;
  // retry before giving up rather than stranding the user on Welcome.
  useEffect(() => {
    if (
      // Mobile-only: web recovers dead RTs via its TS-41719 mount effect, and
      // running this re-fetch there breaks that path (see prop docs).
      !enableConsolidateColdStartRecovery ||
      !isConsolidateAuthTokenStorageOn ||
      !isInitialized ||
      authToken ||
      isLoggedIn ||
      coldStartTokenRefetchedRef.current ||
      loggingOutRef.current
    ) {
      return;
    }

    coldStartTokenRefetchedRef.current = true;

    // Hold the loader for the whole probe window so a recovering valid session
    // shows a spinner, NOT the Welcome/login screen. Without this, authToken is
    // null and isLoggingIn false during the retries, so the nav gate routes on
    // `!authToken` → a login-screen flash for up to the full retry duration.
    // Released below only if no token comes back (the session really is gone).
    setIsLoggingIn(true);
    setHasAttemptedLogin(true);

    (async () => {
      try {
        let token = await getAuthTokenRef.current();

        for (
          let attempt = 0;
          attempt < MAX_COLD_START_TOKEN_RETRIES && !token;
          attempt++
        ) {
          if (isUnmountedRef.current) {
            return;
          }

          await pause(COLD_START_TOKEN_RETRY_INTERVAL_MS);
          token = await getAuthTokenRef.current();
        }

        if (isUnmountedRef.current) {
          return;
        }

        if (!token) {
          // No token after retries — release the loader so the gate can route
          // this now-genuinely-logged-out user to Welcome.
          setIsLoggingIn(false);

          return;
        }

        // isLoggingIn already true; the me query (re-armed by authToken) clears
        // it on completion.
        setAuthToken(token);
      } catch {
        // best-effort; getAuthToken failures fall through to the normal flow.
        setIsLoggingIn(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsolidateAuthTokenStorageOn, isInitialized, authToken, isLoggedIn]);

  const {
    loading,
    error,
    refetch: refetchUser,
  } = useQuery<{
    me: { user: UserType; sessionId: LaneType.UUID };
  }>(initializeUser, {
    client: getClient(),
    skip: !authToken,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      if (data?.me) {
        setMeQuerySucceeded(true);
        probeRetryRef.current = 0;
        // The me query resolved, so the login attempt is finished —
        // clear isLoggingIn here regardless of whether a user came back. When
        // me returns a session but NO user (the new-user / provisioning
        // window, hit constantly under short access-token expiry), leaving
        // isLoggingIn true pinned isCoreLoading and the launch nav gate held an
        // infinite "Entering…" loader. User presence only decides WHERE to
        // route (SignUp vs BottomTabs), not whether to keep spinning.
        setIsLoggingIn(false);
      }

      if (data?.me?.user) {
        // when the user changes, update the state.
        setUser(data.me.user);
        setSessionId(data?.me?.sessionId);
        setIsLoggedIn(true);
        setIsLoggingIn(false);
        setRetryCount(0);

        if (blocks == null || blocks.length === 0) {
          if (fetchBlocks != null) {
            fetchBlocks();
          }
        }

        lDClient.setUser(data?.me?.user?._id);
        analytics.identify(data?.me?.user?._id);
        analytics.setMixpanelSessionId(data?.me?.sessionId);
      }
    },
    onError: error => {
      if (isNotLoggedInError(error)) {
        // If we have a valid authToken but getting not-logged-in error, it's can be a timing issue while creating the user login in the backend
        if (authToken && retryCount < 2) {
          setRetryCount(prev => prev + 1);

          // Clear any existing retry timeout to prevent race conditions
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }

          retryTimeoutRef.current = setTimeout(() => {
            refetchUser().catch(() => undefined /* ignore retry errors */);
          }, 1000);

          return;
        }

        // TS-38909: with the consolidate flag on, Auth0 is the single
        // source of truth for authentication state. A retry-exhausted
        // "not logged in" with an active Auth0 token means lane-server is
        // racing user provisioning (e.g. new SSO signup), not that the
        // user is unauthenticated.
        if (isConsolidateAuthTokenStorageOn) {
          // Auth0 is the source of truth under this flag. A retry-exhausted
          // not-logged-in can mean two different things, distinguished by
          // whether the cached authToken state was present when `me` ran:
          //
          //  - authToken PRESENT: `me` actually ran with a live token and the
          //    server still said "not logged in" after retries → the backend
          //    has no user record yet, i.e. a genuine new-user provisioning
          //    race (any Auth0 user — SSO or password signup). Signal
          //    onboarding via meQuerySucceeded so it routes to /signup/profile.
          //  - authToken ABSENT: `me` was skipped (skip: !authToken) because
          //    the cached token hadn't hydrated yet on cold start. This is an
          //    EXISTING user (SSO or not) mid dead-RT recovery, NOT a new
          //    signup. If Auth0 still holds a valid token, re-arm the me query
          //    so it reloads the real user — setting meQuerySucceeded here
          //    would falsely route an existing user to /signup/profile.
          //
          // A null/failed probe is most often TRANSIENT (SDK hydrating /
          // refresh in flight); retry a few times and only invalidate after
          // the retries are exhausted, so a transient null cannot eject a
          // valid session (spurious logout / isLoggedIn flap).
          const hadAuthTokenAtError = Boolean(authToken);

          const applyProbeSuccess = (
            token: { token: string; jti: string } | string
          ) => {
            probeRetryRef.current = 0;

            if (hadAuthTokenAtError) {
              // New-user provisioning race: let onboarding route to signup.
              setMeQuerySucceeded(true);

              return;
            }

            // Existing user, cached token not hydrated yet. We only need to
            // flip the `skip: !authToken` gate so the me query re-runs and
            // reloads the real user — the actual request auth comes from Auth0
            // via the Apollo link, not this value. Do NOT use storeToken here:
            // under the consolidate flag we deliberately do not persist a
            // legacy AUTH_TOKEN copy (Auth0 owns the token). Setting the gate
            // also avoids meQuerySucceeded, which would mislabel the existing
            // user as a new signup and route them to /signup/profile.
            setAuthToken(token);
          };

          const retryOrInvalidate = () => {
            if (probeRetryRef.current < MAX_PROBE_RETRIES) {
              probeRetryRef.current += 1;

              if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
              }

              retryTimeoutRef.current = setTimeout(() => {
                refetchUser().catch(() => undefined /* ignore retry errors */);
              }, PROBE_RETRY_INTERVAL_MS);

              return;
            }

            probeRetryRef.current = 0;
            setSessionInvalidatedByMeError(true);
            emitter.emit(EVENT_AUTH_TOKEN_INVALID);
          };

          getAuthToken()
            .then(probedToken => {
              if (isUnmountedRef.current) return;

              if (probedToken) {
                applyProbeSuccess(probedToken);

                return;
              }

              retryOrInvalidate();
            })
            .catch(() => {
              if (isUnmountedRef.current) return;

              retryOrInvalidate();
            });
        } else if (authToken && typeof authToken === 'string') {
          try {
            const { exp } = jwtDecode<{
              exp: number;
            }>(authToken);
            const expiringTime = exp * 1000;
            const isTokenExpired = Date.now() > expiringTime;

            if (isTokenExpired) {
              setSessionInvalidatedByMeError(true);
              emitter.emit(EVENT_AUTH_TOKEN_INVALID);
            }
            // FIXME: Log error for datadog, missing stack trace
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            setSessionInvalidatedByMeError(true);
            emitter.emit(EVENT_AUTH_TOKEN_INVALID);
          }
        } else {
          setSessionInvalidatedByMeError(true);
          emitter.emit(EVENT_AUTH_TOKEN_INVALID);
        }
      } else if (authToken) {
        // Me query failed with something other than "not logged in" (e.g. network/blocked).
        // Retry a couple times then treat as invalid session so we force logout → identifier.
        // Keep isLoggingIn true while retrying so we show loading (don't redirect to signup).
        if (retryCount < 2) {
          setRetryCount(prev => prev + 1);

          if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);

          retryTimeoutRef.current = setTimeout(() => {
            refetchUser().catch(() => undefined);
          }, 1000);

          return;
        }

        setSessionInvalidatedByMeError(true);
        emitter.emit(EVENT_AUTH_TOKEN_INVALID);
      }

      setIsLoggingIn(false);
    },
  });

  useEffect(() => {
    if (authToken && !user) {
      setIsLoggingIn(true);
      setHasAttemptedLogin(true);
    }

    if (!authToken) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      setRetryCount(0);

      return; // no need to refetch if there is no token
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    setRetryCount(0);

    refetchUser().catch(() => undefined /* ignore */);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    // todo: this locale logic may be more suitable elsewhere?
    async function updateUserLocale() {
      if (user?.locale) {
        try {
          await i18n.changeLanguage(user.locale);
          await Storage.setItem(StorageKeys.USER_LOCALE, user.locale);
          // FIXME: Log error for datadog, missing stack trace
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err) {
          // error occurred changing locales not worth crashing the app over.
        }
      }
    }

    updateUserLocale();
  }, [user?.locale]);

  async function onAuthToken({ authToken, route, warnings, resolve }: any) {
    // A genuine login/token arrival clears any pending logout state.
    loggingOutRef.current = false;
    await storeToken(authToken);

    if (route) {
      setRoute(route);
    }

    if (warnings) {
      setAuthWarnings(warnings);
    }

    // callback function used to have control over the execution flow
    if (resolve) {
      resolve();
    }
  }

  async function onAuthTokenInvalid() {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    setUser(null);
    setAuthToken(null);
    setRetryCount(0);
    setMeQuerySucceeded(false);
    setIsLoggingIn(false);
    lDClient.unsetUser();
  }

  async function onLogout() {
    // Block the flag-hydration re-fetch from resurrecting the session from
    // surviving Auth0 creds until the user genuinely logs in again.
    loggingOutRef.current = true;
    setHasAttemptedLogin(false);
    setSessionInvalidatedByMeError(false);
    onAuthTokenInvalid();

    setRoute(null);
    setIsLoggingIn(false);
    setIsLoggedIn(false);

    await Storage.removeItem(StorageKeys.PRIMARY_CHANNEL);
    await Storage.removeItem(StorageKeys.SECONDARY_CHANNEL);
  }

  async function storeToken(authToken: string) {
    if (!skipStorageWriteRef.current) {
      await Storage.setItem(StorageKeys.AUTH_TOKEN, authToken);
    }

    setSessionInvalidatedByMeError(false);
    setMeQuerySucceeded(false);
    // A token arriving here (EVENT_AUTH_TOKEN — recovery heal or a
    // fresh Auth0 login) must mark a login attempt and enter the loading state,
    // mirroring checkStorage's token-found path. Without setHasAttemptedLogin
    // the launch nav gate holds on `if (!hasAttemptedLogin) return` — an
    // infinite "Entering…" loader with a valid token. isLoggingIn shows the
    // loader until the me query resolves (which then clears it).
    setIsLoggingIn(true);
    setHasAttemptedLogin(true);
    setAuthToken(authToken);
  }

  useEffect(() => {
    let cancel = false;

    async function updateOAuthUserProfile({ profilePicture }: any) {
      // If profile picture doesn't exists return
      // the user didn't set one in their oAuth Profile
      if (!user || !profilePicture) {
        return;
      }

      const { name } = user.profile;

      try {
        const media = await createAndUploadMedia({
          selectedLibrary: {
            _id: user._id,
            type: LibraryTypeEnum.User,
            name: user.profile.name,
          },
          type: MediaTypeEnum.Image,
          name,
          media: { type: MediaImageContentTypeEnum.png } as ImageType,
          mediaBlob: profilePicture,
          thumbnail: { type: MediaImageContentTypeEnum.jpeg } as ImageType,
          thumbnailBlob: profilePicture,
        });

        if (media?._id) {
          await getClient().mutate({
            mutation: updateUser,
            variables: {
              user: {
                _id: user._id,
                profile: {
                  _id: user.profile._id,
                  image: media._id,
                },
              },
            },
          });
          await refetchUser();
        }
        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (ex) {
        // we can swallow this, failure is tolerated
      }
    }

    // Set the logout guard the moment logout is initiated (before any teardown)
    // so the flag-hydration re-fetch can't resurrect the session.
    function onLoggingOut() {
      loggingOutRef.current = true;
    }

    // listen for login and logout events throughout the app
    emitter.addListener(EVENT_AUTH_BEGIN_LOGOUT, onLoggingOut);
    emitter.addListener(EVENT_LOGOUT, onLogout);
    emitter.addListener(EVENT_AUTH_TOKEN_INVALID, onAuthTokenInvalid);
    emitter.addListener(EVENT_AUTH_TOKEN, onAuthToken);
    emitter.addListener(EVENT_UPLOAD_PROFILE_PHOTO, updateOAuthUserProfile);

    // see if there is an auth token on load.
    async function checkStorage() {
      try {
        let token = await getAuthToken();

        if (cancel) {
          return;
        }

        // Under the consolidate flag, getAuthToken() sources the token
        // from Auth0 (nothing is persisted to Storage). On a relaunch with an
        // expired access token it returns null while the SDK hydrates / the
        // refresh-token exchange is in flight, even though Auth0 holds a valid
        // session. Since this runs once, finalizing authToken=null here makes the
        // nav gate flash Welcome for a logged-in user. Re-probe briefly so the
        // SDK can settle; isInitialized stays false meanwhile, so the gate shows
        // a loader (bounded — not an infinite spinner) rather than the login page.
        //
        // Mobile-only: web already recovers a dead/rotated RT via its TS-41719
        // mount effect. These extra getAuthToken() probes fire concurrent
        // getAccessTokenSilently calls that collide with that mount effect at the
        // auth0-spa-js token lock — the exchange then times out instead of
        // returning invalid_grant, recovery is never classified as unrenewable,
        // and the app hangs on a loader. Web keeps main's single-probe behaviour.
        if (
          !token &&
          isConsolidateAuthTokenStorageOn &&
          enableConsolidateColdStartRecovery
        ) {
          for (
            let attempt = 0;
            attempt < MAX_COLD_START_TOKEN_RETRIES && !token;
            attempt++
          ) {
            await pause(COLD_START_TOKEN_RETRY_INTERVAL_MS);

            if (cancel) {
              return;
            }

            token = await getAuthToken();

            if (cancel) {
              return;
            }
          }
        }

        if (token) {
          setIsLoggingIn(true);
          setHasAttemptedLogin(true);
          setAuthToken(token);
          setIsInitialized(true);

          return;
        }

        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // Tokens not found, no action required.
      }

      if (cancel) {
        return;
      }

      if (jti && jwt) {
        const searchParamToken: any = { jti, token: jwt };

        await storeToken(searchParamToken);
      } else {
        setSessionInvalidatedByMeError(false);
        setAuthToken(currentToken => currentToken ?? null);
      }

      setIsInitialized(true);
    }

    checkStorage();

    instances++;

    if (instances > 1) {
      // this is a warning to developers if they use this hook
      console.warn(
        `You should only have one instances of of useUserData running, currently ${instances}`
      );
    }

    return () => {
      cancel = true;
      instances--;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      emitter.removeListener(EVENT_AUTH_BEGIN_LOGOUT, onLoggingOut);
      emitter.removeListener(EVENT_LOGOUT, onLogout);
      emitter.removeListener(EVENT_AUTH_TOKEN_INVALID, onAuthTokenInvalid);
      emitter.removeListener(EVENT_AUTH_TOKEN, onAuthToken);
      emitter.removeListener(
        EVENT_UPLOAD_PROFILE_PHOTO,
        updateOAuthUserProfile
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hasAnyPermission(
    permissions: PermissionKey[],
    channelId?: string,
    includeAdmin?: boolean
  ): boolean {
    // We need a user and a channelId
    if (!user || !channelId) {
      return false;
    }

    return (
      user!.isSuperUser ||
      hasPermission(user!.roles, permissions, channelId, includeAdmin)
    );
  }

  return {
    refetch: refetchUser,
    user,
    loading,
    error,
    route,
    authToken,
    isInitialized,
    isLoggingIn,
    isLoggedIn,
    hasAttemptedLogin,
    sessionInvalidatedByMeError,
    meQuerySucceeded,
    authWarnings,
    setAuthWarnings,
    hasAnyPermission,
    sessionId,
  } as const;
}
